import React, { useState } from "react";
import { submitSmsConsent } from "../app/services/SmsConsentAPI";
import "./SmsConsent.css";

const SmsConsent = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    organization: "",
    smsConsent: false,
    dataProcessing: false,
    ageConfirmation: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate form after state update
    setTimeout(validateForm, 0);
  };

  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (!value.startsWith("1") && value.length === 10) {
      value = "1" + value;
    }
    if (value.length >= 1) {
      if (value.length <= 1) {
        e.target.value = "+" + value;
      } else if (value.length <= 4) {
        e.target.value = "+" + value.slice(0, 1) + " (" + value.slice(1);
      } else if (value.length <= 7) {
        e.target.value =
          "+" +
          value.slice(0, 1) +
          " (" +
          value.slice(1, 4) +
          ") " +
          value.slice(4);
      } else {
        e.target.value =
          "+" +
          value.slice(0, 1) +
          " (" +
          value.slice(1, 4) +
          ") " +
          value.slice(4, 7) +
          "-" +
          value.slice(7, 11);
      }
    }
    handleInputChange(e);
  };

  const validateForm = () => {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      smsConsent,
      dataProcessing,
      ageConfirmation,
    } = formData;

    const allValid =
      firstName.trim() &&
      lastName.trim() &&
      email.trim() &&
      phoneNumber.trim() &&
      smsConsent &&
      dataProcessing &&
      ageConfirmation;

    setIsFormValid(allValid);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setShowError(false);
    setShowSuccess(false);

    try {
      const response = await submitSmsConsent({
        ...formData,
        consentTimestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });

      setShowSuccess(true);
      setShowError(false);

      // Scroll to success message
      setTimeout(() => {
        document
          .getElementById("successMessage")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage(
        error.message ||
          "There was an error processing your consent. Please try again.",
      );
      setShowError(true);
      setShowSuccess(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sms-consent-container">
      <div className="sms-consent-header">
        <h1>📱 SMS Consent</h1>
        <p>Training and Text Simulation Services</p>
      </div>

      <div className="content-section">
        <h2>🎯 About Our Services</h2>
        <p>
          KinShield provides comprehensive security awareness training through
          personalized simulations designed to help you and your organization
          recognize and respond to various security threats.
        </p>

        <ul className="service-list">
          <li>
            <strong>Phishing Simulations:</strong> Receive realistic phishing
            text messages to test and improve your ability to identify
            suspicious communications.
          </li>
          <li>
            <strong>Security Training:</strong> Get educational SMS messages
            with security tips, best practices, and threat awareness
            information.
          </li>
          <li>
            <strong>Vishing Simulations:</strong> Participate in voice-based
            social engineering training exercises.
          </li>
          <li>
            <strong>Incident Notifications:</strong> Receive alerts about
            security incidents, training schedules, and important updates.
          </li>
        </ul>
      </div>

      <div className="content-section">
        <h2>📋 Message Frequency and Content</h2>
        <p>
          <strong>Frequency:</strong> You may receive up to 10 messages per
          month during active training periods.
        </p>
        <p>
          <strong>Content:</strong> Messages will include training scenarios,
          educational content, security tips, and exercise instructions.
          Messages may contain links for training purposes.
        </p>
        <p>
          <strong>Timing:</strong> Messages may be sent during business hours (9
          AM - 6 PM) in your local timezone, unless participating in specific
          after-hours training scenarios.
        </p>
      </div>

      <div className="legal-text">
        <strong>⚖️ Legal Notice:</strong> By providing your phone number and
        consenting below, you agree to receive text messages from KinShield for
        training and simulation purposes. Message and data rates may apply. You
        can opt-out at any time by replying STOP to any message, or by logging
        into the KinShield app and managing your preferences under Settings →
        Notifications. Reply HELP for assistance. This consent is not required
        as a condition of purchase.
      </div>

      <div className="consent-form">
        <h2>✅ Provide Your Consent</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handlePhoneInput}
              placeholder="+1 (555) 123-4567"
              required
            />
            <small>Format: +1 (XXX) XXX-XXXX</small>
          </div>

          <div className="form-group">
            <label htmlFor="organization">Organization (Optional)</label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleInputChange}
            />
          </div>

          <div className="consent-checkboxes">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="smsConsent"
                name="smsConsent"
                checked={formData.smsConsent}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="smsConsent">
                I consent to receive SMS text messages from KinShield for
                training and simulation purposes. I understand that message and
                data rates may apply.
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="dataProcessing"
                name="dataProcessing"
                checked={formData.dataProcessing}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="dataProcessing">
                I consent to the processing of my personal data (name, email,
                phone number) as described in the Privacy Policy.
              </label>
            </div>

            <div className="checkbox-item">
              <input
                type="checkbox"
                id="ageConfirmation"
                name="ageConfirmation"
                checked={formData.ageConfirmation}
                onChange={handleInputChange}
                required
              />
              <label htmlFor="ageConfirmation">
                I confirm that I am at least 18 years old and have the authority
                to provide this consent.
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Grant SMS Consent"}
          </button>
        </form>

        {showSuccess && (
          <div className="success-message" id="successMessage">
            <h3>✅ Consent Granted Successfully!</h3>
            <p>
              Thank you for providing your consent. You are now enrolled in
              KinShield's SMS training program. You will receive a confirmation
              message shortly.
            </p>
            <p>
              <strong>Remember:</strong> You can opt-out at any time by replying
              STOP to any message.
            </p>
          </div>
        )}

        {showError && (
          <div className="error-message">
            <h3>❌ Error</h3>
            <p>{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="content-section">
        <h2>🔒 Privacy and Data Protection</h2>
        <p>
          Your privacy is important to us. Here's how we protect your
          information:
        </p>
        <ul>
          <li>
            <strong>Secure Storage:</strong> Your phone number and consent data
            are encrypted and stored securely.
          </li>
          <li>
            <strong>Limited Access:</strong> Only authorized personnel have
            access to your information for training purposes.
          </li>
          <li>
            <strong>No Sharing:</strong> We do not share your phone number with
            third parties without your explicit consent.
          </li>
          <li>
            <strong>Retention:</strong> Your data is retained only as long as
            necessary for training purposes or as required by law.
          </li>
          <li>
            <strong>Your Rights:</strong> You can request to view, update, or
            delete your information at any time by contacting us.
          </li>
        </ul>
      </div>

      <div className="content-section">
        <h2>📞 Contact Information</h2>
        <p>If you have questions about SMS consent or need assistance:</p>
        <ul>
          <li>
            <strong>Email:</strong> support@kinprotect.com
          </li>
          <li>
            <strong>Text HELP:</strong> Reply HELP to any training message
          </li>
          <li>
            <strong>Text STOP:</strong> Reply STOP to opt-out at any time
          </li>
          <li>
            <strong>Website:</strong> https://shield.kinprotect.com
          </li>
        </ul>
      </div>

      <div className="sms-consent-footer">
        <p>&copy; 2025 KinShield by KinProtect. All rights reserved.</p>
        <p>
          This consent form complies with TCPA, CAN-SPAM, and applicable
          telecommunications regulations.
        </p>
      </div>
    </div>
  );
};

export default SmsConsent;
